"use client";

import { apiPlugin, StoryblokComponent, storyblokInit, useStoryblok } from "@storyblok/react";
import AppBar from "../../components/AppBar/Appbar";
import Footer from "../global_reference/Footer"
import {
  // BlockTypes, MarkTypes, NodeTypes,
  richTextResolver
} from "@storyblok/richtext"; // Import richTextResolver
import Link from "next/link";

storyblokInit({
  accessToken: process.env.NEXT_PUBLIC_STORYBLOK_TOKEN,
  use: [apiPlugin], // Enable API Plugin
});

const BlogPost = ({slug}) => {

  const story = useStoryblok(`articles/${slug}`, { version: "published", resolve_relations: ["global_reference.reference", "global_header.reference"], cv:new Date().getTime()   }); // Fetch specific blog post

  if (!story || !story.content) {
    return (
      <div className="flex justify-center text-center">
        <p>Loading...</p>
      </div>
    );
  }

  // Function to apply styles dynamically based on the marks
  const applyStyles = (marks) => {
    let styles = {};

    // Loop through all marks and apply corresponding styles
    marks.forEach((mark) => {
      switch (mark.type) {
        case "bold":
          styles.fontWeight = "bold";
          break;
        case "italic":
          styles.fontStyle = "italic";
          break;
        case "underline":
          styles.textDecoration = "underline";
          break;
        case "strikethrough":
          styles.textDecoration = "line-through";
          break;
        case "textStyle":
          if (mark.attrs && mark.attrs.color) {
            styles.color = mark.attrs.color;
          }
          break;
        case "superscript":
          styles.verticalAlign = "super";
          styles.fontSize = "smaller"; // Optional: smaller text for superscript
          break;
        case "subscript":
          styles.verticalAlign = "sub";
          styles.fontSize = "smaller"; // Optional: smaller text for subscript
          break;
        // Add more cases for other marks you might need in the future
        default:
          break;
      }
    });

    return styles;
  };

  const renderRichText = (content) => {
    return content?.map((blok, index) => {

      switch (blok.type) {
        case "paragraph":
          return (
            <div key={index} className="my-5">
              {blok?.content?.map((textBlock, textIndex) => {
                const styles = textBlock?.marks ? applyStyles(textBlock.marks) : {};

                // Handle images
                if (textBlock?.type === "image") {
                  return (
                    <figure key={textIndex} className="my-5">
                      <img
                        src={textBlock?.attrs?.src}
                        alt={textBlock?.attrs?.alt || ""}
                        className="w-full max-w-[100%] h-auto rounded-lg shadow-md object-cover"
                      />
                      {textBlock?.attrs?.caption && (
                        <figcaption className="mt-2 text-sm text-gray-600">
                          {textBlock?.attrs?.caption}
                        </figcaption>
                      )}
                    </figure>
                  );
                }

                // Handle links
                if (textBlock?.marks?.some((mark) => mark.type === "link")) {
                  const linkMark = textBlock?.marks?.find((mark) => mark.type === "link");
                  return (
                    <a
                      key={textIndex}
                      href={linkMark?.attrs?.href}
                      target={linkMark?.attrs?.target || "_blank"}
                      style={styles}
                      rel="noopener noreferrer"
                    >
                      {textBlock.text}
                    </a>
                  );
                }

                return (
                  <span key={textIndex} style={styles}>
                    {textBlock.text}
                  </span>
                );
              })}
            </div>
          );

        case "heading": {
          const headingSizes = {
            1: "36px",
            2: "32px",
            3: "36px",
            4: "28px",
            5: "18px",
            6: "24px",
          };
          const fontSize = headingSizes[blok.attrs?.level] || "36px";

          return (
            <div
              key={index}
              style={{
                fontSize,
                color: "#5D2AE7",
                fontWeight: 700,
                lineHeight: "55px",
              }}
            >
              {blok?.content?.map((textBlock, textIndex) => {
                const styles = textBlock.marks ? applyStyles(textBlock.marks) : {};

                // Handle links
                if (textBlock.marks?.some((mark) => mark.type === "link")) {
                  const linkMark = textBlock.marks.find((mark) => mark.type === "link");
                  return (
                    <a
                      key={textIndex}
                      href={linkMark?.attrs?.href}
                      target={linkMark?.attrs?.target || "_self"}
                      style={styles}
                      rel="noopener noreferrer"
                    >
                      {textBlock.text}
                    </a>
                  );
                }

                return (
                  <span key={textIndex} style={styles}>
                    {textBlock.text}
                  </span>
                );
              })}
            </div>
          );
        }

        case "ordered_list":
        case "unordered_list":
        case "bullet_list":
          return (
            <ol key={index} className="list-decimal">
              {blok?.content?.map((listItem, listIndex) => (
                <li key={listIndex} className="my-5 ml-8">
                  {listItem?.content.map((paragraph, paragraphIndex) => (
                    <span key={paragraphIndex} style={{ marginBottom: "12px" }}>
                      {paragraph?.content?.map((textBlock, textIndex) => (
                        <span
                          key={textIndex}
                          style={{
                            fontWeight:
                              paragraph.content.length > 1 && textIndex === 0 ? "bold" : "normal",
                          }}
                        >
                          {textBlock.text}
                        </span>
                      ))}
                    </span>
                  ))}
                </li>
              ))}
            </ol>
          );

        case "table":
          return (
            <div key={index} className="overflow-x-scroll">
              <table  style={{ width: '100%', borderCollapse: 'collapse', margin: '20px 0' }}>
                <thead>
                  {blok?.content?.find(row => row.type === "tableRow" && row.content.some(cell => cell.type === "tableHeader")) && (
                    <tr>
                      {blok?.content.find(row => row.type === "tableRow").content.map((header, headerIndex) => (
                        header.type === "tableHeader" && (
                          <th key={headerIndex} style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#f2f2f2', textAlign: 'left' }}>
                            {renderRichText(header.content)}
                          </th>
                        )
                      ))}
                    </tr>
                  )}
                </thead>
                <tbody>
                  {blok?.content?.filter(row => row.type === "tableRow" && row.content.some(cell => cell.type === "tableCell")).map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {row?.content?.map((cell, cellIndex) => (
                        cell.type === "tableCell" && (
                          <td key={cellIndex} style={{ border: '1px solid #ddd', padding: '8px' }}>
                            {renderRichText(cell.content)}
                          </td>
                        )
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        default:
          return null;
      }
    });
  };


  let headerBlok = story?.content?.Blog?.find(item => item.component == "global_header");
  let footerBlok = story?.content?.Blog?.find(item => item.component == "global_reference");

  return (
    <div className="bg-asmPageTopBG bg-cover bg-no-repeat overflow-hidden">
      <div className=" m-auto min-h-screen">
        <div
          className=""
          style={{
            backgroundSize: "cover",
            backgroundRepeat: "repeat",
            margin: "auto",
          }}
        >
          {/* Blog Title */}
          <AppBar blok={headerBlok} />
          {/* Map over Blog Array and Render Content */}
          <div className="mt-32">
            {story?.content?.Blog?.filter(item => !["global_header", "global_reference"].includes(item.component))?.map((blok) => (
              <div key={blok._uid} className="mb-6">
                <div className="flex bg-contain relative justify-center w-full">
                  <div className="bg-footer-bg h-1/2 w-full absolute left-0 self-center lg:w-[100vw]"></div>
                  <div className="flex flex-col xl:flex-row  z-10 lg:max-w-7xl w-full lg:mx-24 xl:gap-10 items-center">
                    <img src={blok?.blogImg?.filename} className="h-[180px] w-[180px] lg:w-[513px] lg:h-[513px] 2xl:ml-0 shadow-2xl rounded-2xl" />
                    <div className="flex flex-col z-10 justify-center items-center xl:items-start relative gap-6 h-full">
                      <div className="lg:text-[48px] w-full text-[24px]  !text-white font-lato font-bold text-center lg:p-0 lg:text-start self-center px-2 mt-2 lg:mt-0">
                        {blok.titleText1}
                      </div>
                      <div className="mt-4 font-lato flex flex-row justify-start items-center lg:absolute bottom-10 w-max justify-self-center xl:justify-self-auto">
                        <div className="hidden md:block lg:block"></div>
                        <div className="flex ">
                          {blok?.authorPic?.filename && <div className="h-[48px] w-[48px] lg:mt-2 mr-4">
                            <img src={blok?.authorPic?.filename} />
                          </div>}
                          <div className="">
                            <div className="lg:text-[24px] font-bold">
                              {blok.authorName}
                            </div>
                            <div className="lg:text-[16px]">{blok.authorRole}</div>
                          </div>
                          <div className="ml-3 lg:ml-10 h-[51px]">
                            <img src={"/images/smallLine.svg"} />
                          </div>
                          <div className="lg:ml-4">
                            <img src={"/images/date.svg"} />
                          </div>
                          <div className="lg:my-3 lg:text-[24px] font-lato my-4 min-w-max">
                            {blok.date}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex justify-center">
                  <div className="xl:flex xl:justify-center m-auto lg:max-w-7xl gap-10 lg:mx-24 w-full">
                    <div className="lg:w-[100%] lg:p-10 p-4 lg:mt-20 bg-[#F7F4FF] rounded-[34px] shadow-lg mb-10">
                      <div className="prose max-w-none">
                        <div>{renderRichText(blok?.content1?.content)}</div>
                      </div>
                    </div>
                    <div className=" ">
                      <div className="flex lg:mt-20 mt-10 font-lato lg:justify-start">
                        <div className="mt-3 mr-2 ml-5 lg:ml-0">
                          Share this post on:
                        </div>
                        <img
                          src={"/images/Facebook.svg"}
                          className="w-[50px] h-[50px] cursor-pointer"
                        />
                        <img
                          src={"/images/Instagram.svg"}
                          className="w-[50px] h-[50px] cursor-pointer"
                        />
                        <img
                          src={"/images/LinkedIn.svg"}
                          className="w-[50px] h-[50px] cursor-pointer"
                        />
                      </div>
                      <div className="lg:w-[318px] lg:h-[300px] bg-footer-bg rounded-[25px] lg:mt-6 p-4 lg:mr-20 m-4 xl:m-0">
                        <div className="!text-white text-[32px] font-bold font-lato lg:ml-6">
                        Need help in improving your cybersecurity posture?
                        </div>
                        <a href="/connect" >
                          <button className="bg-[#E56717] lg:w-[231px]  h-[45px] rounded-[5px] lg:ml-6 font-lato !text-white lg:text-[24px] mt-6 w-full">
                            Book a Call
                          </button>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <Footer blok={footerBlok} />
    </div>
  );
};

export default BlogPost;
